import { notification, TableProps } from 'antd';
import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import VersionHistoryFooter from '../../../foundation/components/version_history_footer/VersionHistoryFooter';
import { boldColValueRenderer } from '../../../foundation/utils/boldColValueRenderer';
import {
  useGetGeneFactsOverviewQuery,
  useGetOtherVariantsQuery,
  useGetVariantDetailQuery,
  useGetVariantLinkoutsQuery,
  useGetVariantProteinDomainsQuery,
  useUpdateVariantProteinDomainsMutation,
} from '../../api/variantApi';
import CollapseHeaderForSections from '../components/CollapseHeaderForSections';
import CollapsePanel from '../components/CollapsePanel';
import DetailCard from '../components/DetailCard';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../constants';
import { VARIANT_CONSEQUENCE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { ProteinDomain } from '../redux';
import { OtherVariantsTableData } from '../redux';
import ClinVarGeneDistributionSection from './ClinVarGeneDistributionSection';

const GeneProteinFactsSection = () => {
  const params = useParams();

  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  // Read other variants directly from RTK Query.
  const { data: otherVariantsResponse, isLoading: isOtherVariantsLoading } =
    useGetOtherVariantsQuery(
      { variant_id: params.vid },
      {
        skip:
          !params.vid ||
          variant?.consequence !== VARIANT_CONSEQUENCE_TYPES.MISSENSE,
      },
    );
  const clinvarSearchResults = otherVariantsResponse?.variants ?? [];
  const otherVariantsFailureMessage =
    otherVariantsResponse?.failure_message ?? null;

  const { data: geneFactsOverview } = useGetGeneFactsOverviewQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { data: variantLinkouts } = useGetVariantLinkoutsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { data: proteinDomainsData, isLoading: isProteinDomainsQueryLoading } =
    useGetVariantProteinDomainsQuery(
      { variant_id: params.vid },
      { skip: !params.vid },
    );
  const dataSource = proteinDomainsData?.protein_domains;
  const sourceVersion = proteinDomainsData?.source_version;
  const versionDate = proteinDomainsData?.date;

  const [
    updateVariantProteinDomains,
    { isLoading: isProteinDomainsMutationLoading },
  ] = useUpdateVariantProteinDomainsMutation();

  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.MOLECULAR_IMPACT);
  const { canEditWorkflow } = useVariantAccess();
  const isUpdateDisabled = !canEditWorkflow || stageInfo?.status === 'accepted';

  const otherVariantsColumns = [
    {
      title: 'Known variant in same codon',
      dataIndex: 'variation_name',
      key: 'variation_name',
      render: (text: string, record: OtherVariantsTableData) =>
        record.other_variant_id ? (
          <Link
            to={`/variant-workflow/${record.other_variant_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="interpretation__intervar-link__anchor"
          >
            {text}
          </Link>
        ) : (
          text
        ),
    },
    {
      title: 'Consequence',
      dataIndex: 'variant_consequence',
      key: 'variant_consequence',
    },
    {
      title: 'Clinvar interpretation',
      dataIndex: 'interpretation',
      key: 'interpretation',
    },
    {
      title: 'Review State',
      dataIndex: 'review_status',
      key: 'review_status',
    },
    {
      title: 'Variation id',
      dataIndex: 'variation_id',
      key: 'variation_id',
      render: (text: string, record: OtherVariantsTableData) => (
        <a
          href={record.linkout}
          target="_blank"
          rel="noopener noreferrer"
          className="interpretation__intervar-link__anchor"
        >
          {text}
        </a>
      ),
    },
  ];

  const proteinDomainColumns: TableProps<ProteinDomain>['columns'] = useMemo(
    () => [
      {
        title: 'Protein domain name',
        dataIndex: 'protein_domain_name',
        key: 'protein_domain_name',
        render: boldColValueRenderer,
        width: 450,
      },
      {
        title: 'Protein coordinates',
        dataIndex: 'protein_coordinates',
        key: 'protein_coordinates',
        width: 150,
        render: (text) => {
          return <span>{JSON.stringify(text)}</span>;
        },
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: 120,
      },
      {
        title: 'InterProID',
        dataIndex: 'interpro_id',
        key: 'interpro_id',
        width: 120,
        render: (text, record) => {
          if (record.interpro_url) {
            return (
              <a
                href={record.interpro_url}
                target="_blank"
                rel="noopener noreferrer"
                className="gene-facts-ncbi-link__anchor"
              >
                {text}
              </a>
            );
          }
          return text;
        },
      },
    ],
    [],
  );

  const handleUpdateDataClick = async () => {
    try {
      if (params.vid) {
        await updateVariantProteinDomains({ variant_id: params.vid }).unwrap();
        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: NOTIFICATION_MESSAGES.PROTEIN_DOMAIN_UPDATED,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <DetailCard>
      <div className="gene-protein-facts-panel">
        <CollapsePanel
          title={<CollapseHeaderForSections title={'Gene / protein facts'} />}
          panelKey="gene-protein-facts"
          defaultActiveKey="gene-protein-facts"
        >
          <div style={{ paddingLeft: 25 }}>
            <div className="interpretation__intervar-link">
              {geneFactsOverview?.clinvar_gene_url && (
                <a
                  href={geneFactsOverview.clinvar_gene_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="interpretation__intervar-link__anchor"
                >
                  ClinVar{' '}
                </a>
              )}
              {variantLinkouts?.decipher_gene_url && (
                <a
                  href={variantLinkouts.decipher_gene_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="interpretation__intervar-link__anchor"
                >
                  Decipher (GRCh38){' '}
                </a>
              )}
              {geneFactsOverview?.gnomad_gene_url && (
                <a
                  href={geneFactsOverview.gnomad_gene_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="interpretation__intervar-link__anchor"
                >
                  gnomAD{' '}
                </a>
              )}
              {geneFactsOverview?.interpro_gene_protein_url && (
                <a
                  href={geneFactsOverview.interpro_gene_protein_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="interpretation__intervar-link__anchor"
                >
                  InterPro
                </a>
              )}
            </div>

            <div style={{ marginTop: '24px' }}>
              <CollapseHeaderForSections
                title={'Other informative variants'}
                style={{ marginBottom: '16px' }}
              />
              {otherVariantsFailureMessage && (
                <div style={{ color: 'red', marginBottom: '16px' }}>
                  {otherVariantsFailureMessage}
                </div>
              )}
              {variant?.consequence === VARIANT_CONSEQUENCE_TYPES.MISSENSE &&
                !otherVariantsFailureMessage && (
                  <ResizableTable
                    dataSource={clinvarSearchResults}
                    columns={otherVariantsColumns}
                    pagination={false}
                    rowKey="variation_id"
                    size="small"
                    loading={isOtherVariantsLoading}
                    persistenceKey="gene-facts-other-variants"
                    locale={{
                      emptyText: isOtherVariantsLoading
                        ? 'Searching ClinVar...'
                        : 'no other informative variants in same codon',
                    }}
                  />
                )}
              {/* ClinVarHotspotSection temporarily removed until underlying
                  ClinVar data issues are resolved. Re-enable by rendering
                  <ClinVarHotspotSection /> here. */}
              <ClinVarGeneDistributionSection />
            </div>
            <div style={{ marginTop: '24px' }}>
              <CollapseHeaderForSections
                title={'Protein domain / regulatory feature'}
                style={{ marginBottom: '16px' }}
              />
              <div className="molecular-impact__protein-domain-regulatory">
                <ResizableTable
                  columns={proteinDomainColumns}
                  dataSource={dataSource}
                  pagination={false}
                  rowKey={(record) => record.protein_domain_name}
                  className="molecular-impact__protein-domain-regulatory-table"
                  loading={
                    isProteinDomainsQueryLoading ||
                    isProteinDomainsMutationLoading
                  }
                  persistenceKey="gene-facts-protein-domains"
                />
              </div>

              <VersionHistoryFooter
                historyInfo={`${sourceVersion ? sourceVersion : ''} ${versionDate ? versionDate : ''}`}
                onUpdateClick={handleUpdateDataClick}
                disabled={isUpdateDisabled}
              />
            </div>
          </div>
        </CollapsePanel>
      </div>
    </DetailCard>
  );
};

export default GeneProteinFactsSection;
